import type {
  VendorUploadCreateInput,
  VendorUploadCreateResult,
  VendorUploadInfo,
} from '@peles/shared';

/**
 * Public (no-auth) vendor file-upload API. Uses plain fetch — the authenticated
 * apiClient would redirect to /login on 401 and attach producer tokens, neither
 * of which applies to the tokenized vendor link.
 */

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = 'Request failed';
    try {
      const body = (await res.json()) as { message?: string; error?: { message?: string } };
      message = body.error?.message ?? body.message ?? message;
    } catch {
      // keep default message
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export async function getVendorUploadInfo(token: string): Promise<VendorUploadInfo> {
  const res = await fetch(`/api/v1/vendor-uploads/${encodeURIComponent(token)}`);
  return handle<VendorUploadInfo>(res);
}

/**
 * Where to PUT the bytes, and the handle to send back afterwards.
 *
 * ⚠️ **One of `s3Key` and `fileId`, and this page never chooses.** A link asked
 * for from a conversation presigns through the file library so the file becomes
 * a real library file the thread can carry; every other link presigns into the
 * request's own prefix. Which one is a fact about the link, decided on the
 * server — this echoes back whatever it was handed, and the server checks it
 * against the request either way.
 */
export async function getVendorUploadUrl(
  token: string,
  fileName: string,
  mimeType: string,
): Promise<{ uploadUrl: string; s3Key?: string; fileId?: string }> {
  const res = await fetch(`/api/v1/vendor-uploads/${encodeURIComponent(token)}/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName, mimeType }),
  });
  return handle<{ uploadUrl: string; s3Key?: string; fileId?: string }>(res);
}

export async function createVendorUpload(
  token: string,
  input: VendorUploadCreateInput,
): Promise<VendorUploadCreateResult> {
  const res = await fetch(`/api/v1/vendor-uploads/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handle<VendorUploadCreateResult>(res);
}
